import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Grid,
  Divider,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import QRCode from 'react-qr-code';
import moment from 'moment';

const OTPGenerator = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [visitPurpose, setVisitPurpose] = useState('');
  const [accessFrom, setAccessFrom] = useState(moment().add(1, 'hour'));
  const [accessTo, setAccessTo] = useState(moment().add(3, 'hour'));
  const [generatedOTP, setGeneratedOTP] = useState('');
  const [otpLink, setOtpLink] = useState('');
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  
  const { userUnit } = useSelector(state => state.auth);
  
  const steps = ['訪客資訊', '訪問時間', '生成通行證'];
  
  // 驗證當前步驟的數據
  const validateStep = () => {
    const newErrors = {};
    
    if (activeStep === 0) {
      if (!visitorName.trim()) newErrors.visitorName = '請輸入訪客姓名';
      if (!visitorPhone.trim()) newErrors.visitorPhone = '請輸入訪客電話';
      if (!visitPurpose.trim()) newErrors.visitPurpose = '請選擇訪問目的';
    } else if (activeStep === 1) {
      if (!accessFrom) newErrors.accessFrom = '請選擇開始時間';
      if (!accessTo) newErrors.accessTo = '請選擇結束時間';
      if (accessFrom && accessTo && accessFrom.isAfter(accessTo)) {
        newErrors.accessTo = '結束時間必須晚於開始時間';
      }
      if (accessFrom && accessFrom.isBefore(moment())) {
        newErrors.accessFrom = '開始時間必須是未來時間';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // 處理下一步
  const handleNext = () => {
    if (validateStep()) {
      if (activeStep === steps.length - 2) {
        // 最後一步之前生成OTP
        generateOTP();
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };
  
  // 處理返回上一步
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // 生成OTP
  const generateOTP = () => {
    // 在實際應用中，這將是API調用
    const otpCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    setGeneratedOTP(otpCode);
    
    // 生成可分享的鏈接
    const baseUrl = window.location.origin;
    setOtpLink(`${baseUrl}/visitor-access?otp=${otpCode}&from=${accessFrom.unix()}&to=${accessTo.unix()}&unit=${userUnit?.number}`);
  };
  
  // 重設所有表單
  const handleReset = () => {
    setActiveStep(0);
    setVisitorName('');
    setVisitorPhone('');
    setVisitPurpose('');
    setAccessFrom(moment().add(1, 'hour'));
    setAccessTo(moment().add(3, 'hour'));
    setGeneratedOTP('');
    setOtpLink('');
    setErrors({});
  };
  
  // 複製到剪貼板
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setShowSuccess(true);
    });
  };
  
  // 調用分享API (如果支援)
  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: '訪客通行證',
        text: `${visitorName}, 您被邀請訪問${userUnit?.number}單位。訪問密碼: ${generatedOTP}`,
        url: otpLink
      }).catch(error => console.log('分享出錯:', error));
    } else {
      copyToClipboard(otpLink);
    }
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>訪客通行證</Typography>
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {activeStep === 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                訪客資訊
              </Typography>
              <TextField
                fullWidth
                label="訪客姓名"
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                margin="normal"
                error={!!errors.visitorName}
                helperText={errors.visitorName}
              />
              <TextField
                fullWidth
                label="訪客電話"
                value={visitorPhone}
                onChange={(e) => setVisitorPhone(e.target.value)}
                margin="normal"
                error={!!errors.visitorPhone}
                helperText={errors.visitorPhone}
              />
              <FormControl fullWidth margin="normal" error={!!errors.visitPurpose}>
                <InputLabel>訪問目的</InputLabel>
                <Select
                  value={visitPurpose}
                  onChange={(e) => setVisitPurpose(e.target.value)}
                  label="訪問目的"
                >
                  <MenuItem value="family">家庭訪問</MenuItem>
                  <MenuItem value="friend">朋友訪問</MenuItem>
                  <MenuItem value="delivery">送貨</MenuItem>
                  <MenuItem value="maintenance">維修服務</MenuItem>
                  <MenuItem value="other">其他</MenuItem>
                </Select>
                {errors.visitPurpose && <FormHelperText>{errors.visitPurpose}</FormHelperText>}
              </FormControl>
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                訪問時間
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                訪客只能在指定的時間內使用一次通行證
              </Typography>
              
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MobileDateTimePicker
                      label="通行開始時間"
                      value={accessFrom}
                      onChange={setAccessFrom}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          fullWidth 
                          margin="normal"
                          error={!!errors.accessFrom}
                          helperText={errors.accessFrom}
                        />
                      )}
                      minDateTime={moment()}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MobileDateTimePicker
                      label="通行結束時間"
                      value={accessTo}
                      onChange={setAccessTo}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          fullWidth 
                          margin="normal"
                          error={!!errors.accessTo}
                          helperText={errors.accessTo}
                        />
                      )}
                      minDateTime={accessFrom || moment()}
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Box>
          )}
          
          {activeStep === 2 && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                通行證已成功生成！請與訪客分享。
              </Alert>
              
              <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 2, textAlign: 'center' }}>
                <Box sx={{ mb: 3 }}>
                  <VpnKeyIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h3" gutterBottom>
                    {generatedOTP}
                  </Typography>
                  <Chip 
                    label={`有效期: ${accessFrom.format('MM/DD HH:mm')} - ${accessTo.format('MM/DD HH:mm')}`} 
                    color="secondary" 
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ my: 2 }}>
                  <QRCode 
                    value={otpLink} 
                    size={160}
                    level="H"
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    掃描二維碼或輸入密碼訪問
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 3 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<ShareIcon />}
                    onClick={shareLink}
                    sx={{ mr: 1 }}
                  >
                    分享通行證
                  </Button>
                  <Button 
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(generatedOTP)}
                  >
                    複製密碼
                  </Button>
                </Box>
              </Paper>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">訪客資訊</Typography>
                <Typography variant="body2">
                  姓名: {visitorName} | 電話: {visitorPhone}
                </Typography>
                <Typography variant="body2">
                  訪問目的: {
                    visitPurpose === 'family' ? '家庭訪問' :
                    visitPurpose === 'friend' ? '朋友訪問' :
                    visitPurpose === 'delivery' ? '送貨' :
                    visitPurpose === 'maintenance' ? '維修服務' : '其他'
                  }
                </Typography>
              </Box>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              返回
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            
            {activeStep === steps.length - 1 ? (
              <Button onClick={handleReset}>
                建立新通行證
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                {activeStep === steps.length - 2 ? '生成通行證' : '下一步'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSuccess(false)}
        message="已複製到剪貼板"
      />
    </Box>
  );
};

export default OTPGenerator; 